(() => {
 'use strict';
 if (window.sfgGardenReady) return;
 window.sfgGardenReady = true;
 const mobile = matchMedia('(max-width: 767px)');
 const reduced = matchMedia('(prefers-reduced-motion: reduce)');
 const connection = navigator.connection;
 const films = new Map();
 const stepMotions = new WeakMap();
 let current = null, preferred = null;
 const automatic = () => !reduced.matches && !connection?.saveData && !/^(slow-)?2g$/.test(connection?.effectiveType || '');
 const allowed = state => {
  if (!state.element.isConnected || state.ratio < .18 || state.element.closest('[hidden]') || document.hidden || state.paused) return false;
  const step = state.element.closest('[data-step]');
  if (step && step.dataset.step !== step.closest('[data-sfg-routine]').dataset.active) return false;
  return automatic() || state.user;
 };
 function paint(state) {
  const playing = !state.video.paused;
  state.button.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
  state.button.querySelector('[data-play-icon]').hidden = playing;
  state.button.querySelector('[data-pause-icon]').hidden = !playing;
 }
 function release(state) {
  if (!state) return;
  state.time = Number.isFinite(state.video.currentTime) ? state.video.currentTime : 0;
  state.video.pause();
  if (state.loaded) { state.loaded = false; state.video.removeAttribute('src'); state.video.load(); }
  paint(state);
 }
 function sync() {
  for (const [element, state] of films) if (!element.isConnected) { release(state); observer.unobserve(element); films.delete(element); }
  const choices = [...films.values()].filter(allowed);
  const next = choices.includes(preferred) ? preferred : choices.includes(current) && current.ratio > .35 ? current : choices.sort((a,b) => b.ratio - a.ratio)[0] || null;
  if (next !== current) { release(current); current = next; }
  if (!current) return;
  const state = current;
  if (!state.loaded) {
   state.loaded = true;
   state.video.src = mobile.matches ? state.video.dataset.mobile : state.video.dataset.desktop;
   state.video.load();
  }
  if (state.video.paused) state.video.play().then(() => { if (current !== state || !allowed(state)) state.video.pause(); }).catch(() => paint(state));
 }
 const observer = new IntersectionObserver(entries => {
  entries.forEach(entry => { const state = films.get(entry.target); if (state) state.ratio = entry.isIntersecting ? entry.intersectionRatio : 0; });
  sync();
 }, {threshold:[0,.18,.35,.6,.85,1]});
 function bindFilms() {
  document.querySelectorAll('[data-sfg-film]').forEach(element => {
   if (films.has(element)) return;
   const video = element.querySelector('video'), button = element.querySelector('button');
   const state = {element,video,button,ratio:0,paused:false,user:false,loaded:false,time:0};
   video.muted = true; video.playsInline = true;
   films.set(element,state);
   button.addEventListener('click', () => {
    if (!video.paused) { state.paused = true; state.user = false; if (preferred === state) preferred = null; }
    else {
     const step = element.closest('[data-step]');
     if (step) selectStep(step.closest('[data-sfg-routine]'), Number(step.dataset.step));
     state.paused = false; state.user = true; preferred = state;
    }
    sync();
   });
   video.addEventListener('play', () => paint(state));
   video.addEventListener('pause', () => paint(state));
   video.addEventListener('ended', () => advanceStep(state));
   video.addEventListener('loadedmetadata', () => { if (state.time && state.time < video.duration) video.currentTime = state.time; });
   video.addEventListener('error', () => { if (state.loaded) element.querySelector('[role=status]').hidden = false; });
   video.addEventListener('playing', () => { element.querySelector('[role=status]').hidden = true; });
   observer.observe(element); paint(state);
  });
 }
 function clearStepMotion(root) {
  const motion = stepMotions.get(root);
  if (!motion) return;
  stepMotions.delete(root);
  motion.animations.forEach(animation => animation.cancel());
  motion.overlay.remove();
 }
 function stepSnapshot(panel) {
  const video = panel.querySelector('video'), overlay = document.createElement('div');
  overlay.className = 'sfg-step-transition';
  overlay.setAttribute('aria-hidden','true');
  overlay.style.height = `${panel.offsetHeight}px`;
  let frame;
  if (video.readyState >= 2 && video.videoWidth) {
   const canvas = document.createElement('canvas');
   const scale = Math.min(devicePixelRatio || 1,2);
   canvas.width = Math.round(panel.clientWidth * scale); canvas.height = Math.round(panel.clientHeight * scale);
   const context = canvas.getContext('2d');
   if (context) {
    const fit = Math.max(canvas.width / video.videoWidth,canvas.height / video.videoHeight);
    context.drawImage(video,(canvas.width - video.videoWidth * fit) / 2,(canvas.height - video.videoHeight * fit) / 2,video.videoWidth * fit,video.videoHeight * fit);
    frame = canvas;
   }
  }
  if (!frame) { frame = document.createElement('img'); frame.src = video.poster; frame.alt = ''; }
  overlay.append(frame);
  overlay.append(panel.querySelector('.sfg-step-number').cloneNode(true));
  panel.closest('.sfg-steps').append(overlay);
  return overlay;
 }
 function animateStep(root,panel,overlay,direction) {
  const style = getComputedStyle(root);
  const options = {duration:parseFloat(style.getPropertyValue('--sfg-step-duration')) || 360,easing:style.getPropertyValue('--sfg-step-ease').trim() || 'ease-out'};
  const animations = [
   panel.querySelector('.sfg-film').animate([{transform:`translateX(${direction * 30}px) scale(1.025)`,opacity:.35},{transform:'translateX(0) scale(1)',opacity:1}],options),
   overlay.animate([{opacity:1},{opacity:0}],options),
   overlay.firstElementChild.animate([{transform:'translateX(0) scale(1)'},{transform:`translateX(${-direction * 24}px) scale(1.025)`}],options)
  ];
  const motion = {overlay,animations};
  stepMotions.set(root,motion);
  Promise.all(animations.map(animation => animation.finished)).catch(() => {}).then(() => {
   if (stepMotions.get(root) === motion) { stepMotions.delete(root); overlay.remove(); }
  });
 }
 function selectStep(root, index, direction = 0) {
  clearStepMotion(root);
  const previousIndex = Number(root.dataset.active);
  const previous = films.get(root.querySelector(`[data-step="${root.dataset.active}"] [data-sfg-film]`));
  const changed = root.dataset.active !== String(index);
  const oldPanel = previous?.element.closest('.sfg-step-film');
  // Freeze just the outgoing frame so the transition never needs two live videos.
  const overlay = changed && mobile.matches && !reduced.matches && !document.hidden && oldPanel && !oldPanel.hidden && oldPanel.offsetWidth ? stepSnapshot(oldPanel) : null;
  root.dataset.active = String(index);
  root.style.setProperty('--sfg-step-index',index);
  root.querySelectorAll('[data-step-tab]').forEach((button,i) => { button.setAttribute('aria-selected', String(i === index)); button.tabIndex = i === index ? 0 : -1; });
  root.querySelectorAll('[data-step]').forEach((article,i) => {
   article.classList.toggle('is-active',i === index);
   const panel = article.querySelector('.sfg-step-film');
   panel.hidden = mobile.matches && i !== index;
   if (mobile.matches) { panel.setAttribute('role','tabpanel'); panel.setAttribute('aria-labelledby', root.querySelectorAll('[data-step-tab]')[i].id); }
   else { panel.removeAttribute('role'); panel.removeAttribute('aria-labelledby'); }
   const state = films.get(article.querySelector('[data-sfg-film]'));
   if (state) {
    // Every viewport plays one complete step before advancing.
    state.video.loop = false;
    if (changed && i === index) {
     state.time = 0;
     state.paused = previous?.paused || false;
     if (state.video.readyState) state.video.currentTime = 0;
    }
   }
   if (state && i !== index) { state.user = false; if (preferred === state) preferred = null; }
  });
  if (overlay) animateStep(root,root.querySelector(`[data-step="${index}"] .sfg-step-film`),overlay,direction || (index > previousIndex ? 1 : -1));
  // New visible panel gets its intersection value in the next observer callback.
  sync();
 }
 function advanceStep(state) {
  const root = state.element.closest('[data-sfg-routine]');
  if (!root || !automatic() || current !== state || !allowed(state) || !state.video.ended || root.dataset.swiping) return;
  // A keyboard user keeps control of the focused video's play button.
  if (state.element.contains(document.activeElement) && document.activeElement.matches(':focus-visible')) return;
  selectStep(root, (Number(root.dataset.active) + 1) % root.querySelectorAll('[data-step]').length, 1);
 }
 function bindStepSwipe(root) {
  const track = root.querySelector('.sfg-steps');
  let gesture = null;
  let press = null;
  const clearPress = () => { if (press) delete press.button.dataset.sfgPressed; press = null; };
  root.addEventListener('pointerdown', event => {
   clearPress();
   const button = event.target.closest('.sfg-step-picker button,.sfg-play');
   if (!mobile.matches || !event.isPrimary || event.button !== 0 || !button) return;
   press = {button,x:event.clientX,y:event.clientY};
   button.dataset.sfgPressed = 'true';
  });
  root.addEventListener('pointermove', event => { if (press && Math.hypot(event.clientX - press.x,event.clientY - press.y) > 10) clearPress(); });
  ['pointerup','pointercancel','pointerleave','focusout'].forEach(type => root.addEventListener(type,clearPress));
  const clear = () => { gesture = null; delete root.dataset.swiping; };
  track.addEventListener('pointerdown', event => {
   if (!event.isPrimary) { clear(); return; }
   const panel = event.target.closest('.sfg-step-film');
   if (!mobile.matches || !panel || event.button !== 0 || event.target.closest('button,a')) return;
   gesture = {id:event.pointerId,x:event.clientX,y:event.clientY};
   root.dataset.swiping = 'true';
   panel.setPointerCapture(event.pointerId);
  });
  track.addEventListener('pointermove', event => {
   if (!gesture || event.pointerId !== gesture.id) return;
   const dx = Math.abs(event.clientX - gesture.x), dy = Math.abs(event.clientY - gesture.y);
   if (dy > 12 && dy > dx) clear();
  });
  track.addEventListener('pointerup', event => {
   if (!gesture || event.pointerId !== gesture.id) return;
   const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
   clear();
   if (mobile.matches && Math.abs(dx) >= 44 && Math.abs(dx) > Math.abs(dy) * 1.3) {
    const count = root.querySelectorAll('[data-step]').length;
    selectStep(root, (Number(root.dataset.active) + (dx < 0 ? 1 : -1) + count) % count, dx < 0 ? 1 : -1);
   } else {
    const state = films.get(root.querySelector(`[data-step="${root.dataset.active}"] [data-sfg-film]`));
    if (state) advanceStep(state);
   }
  });
  track.addEventListener('pointercancel', clear);
  track.addEventListener('lostpointercapture', clear);
  mobile.addEventListener('change', clear);
 }
 function bindTabKeys(list, select, buttons) {
  list.addEventListener('keydown', event => {
   if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
   event.preventDefault();
   const active = buttons.findIndex(button => button.getAttribute('aria-selected') === 'true');
   const index = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length-1 : (active + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
   select(index,event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0); buttons[index].focus();
  });
 }
 const fitCopy = {
  space:['THE EVERYDAY FOOTPRINT','12.87″ tall','With the lid closed. Allow clearance above and around the unit.'],
  care:['MADE FOR EVERYDAY CARE','Lift it out.','The inner bucket is removable for emptying and cleaning.'],
  cost:['A LITTLE ROUTINE CARE','Keep it working.','Allow for Microbe Starter, replacement filters and electricity.']
 };
 function init() {
  bindFilms();
  document.querySelectorAll('[data-sfg-routine]:not([data-bound])').forEach(root => {
   root.dataset.bound = 'true'; root.classList.add('is-ready');
   root.querySelector('.sfg-step-picker').hidden = false;
   const buttons = [...root.querySelectorAll('[data-step-tab]')];
   buttons.forEach((button,index) => button.addEventListener('click', () => selectStep(root,index)));
   bindTabKeys(root.querySelector('[role=tablist]'), (index,direction) => selectStep(root,index,direction),buttons);
   bindStepSwipe(root);
   selectStep(root,0);
  });
  document.querySelectorAll('[data-sfg-inside]:not([data-bound])').forEach(root => {
   root.dataset.bound = 'true';
   const buttons = [...root.querySelectorAll('[data-inside-tab]')];
   const select = index => {
    buttons.forEach((button,i) => { button.setAttribute('aria-selected',String(i === index)); button.tabIndex = i === index ? 0 : -1; });
    root.querySelectorAll('[data-inside-panel]').forEach((panel,i) => { panel.hidden = i !== index; }); sync();
   };
   buttons.forEach((button,index) => button.addEventListener('click',() => select(index)));
   bindTabKeys(root.querySelector('[role=tablist]'),select,buttons);
  });
  document.querySelectorAll('[data-sfg-fit]:not([data-bound])').forEach(root => {
   root.dataset.bound = 'true';
   root.querySelectorAll('[data-fit-question]').forEach(details => details.addEventListener('toggle',() => {
    if (!details.open) return;
    const key = details.dataset.fitQuestion, copy = fitCopy[key];
    root.dataset.focus = key;
    root.querySelectorAll('[data-fit-question]').forEach(other => { if (other !== details) other.open = false; });
    root.querySelectorAll('[data-marker]').forEach(marker => { marker.hidden = marker.dataset.marker !== key; });
    ['label','value','note'].forEach((part,i) => { root.querySelector(`[data-fit-${part}]`).textContent = copy[i]; });
   }));
  });
  document.querySelectorAll('[data-sfg-voices]:not([data-bound])').forEach(root => {
   root.dataset.bound = 'true';
   const quotes = [...root.querySelectorAll('.sfg-quote')]; if (!quotes.length) return;
   let active = 0;
   const select = () => { quotes.forEach((quote,i) => { quote.hidden = i !== active; }); root.querySelector('[data-quote-count]').textContent = `${active+1} / ${quotes.length}`; };
   root.querySelector('.sfg-quote-controls').hidden = quotes.length < 2;
   root.querySelector('[data-quote-prev]').addEventListener('click', () => { active = (active - 1 + quotes.length) % quotes.length; select(); });
   root.querySelector('[data-quote-next]').addEventListener('click', () => { active = (active + 1) % quotes.length; select(); }); select();
  });
  // Calendar estimate derived from the stated business-day range, with the range always visible.
  document.querySelectorAll('[data-sfg-delivery-date]').forEach(target => {
   const businessDate = days => { const date = new Date(); for (let i=0;i<days;) { date.setDate(date.getDate()+1); if (date.getDay() !== 0 && date.getDay() !== 6) i++; } return date; };
   const format = new Intl.DateTimeFormat('en-US',{month:'short',day:'numeric'});
   target.textContent = ` · ${format.format(businessDate(7))}–${format.format(businessDate(12))}`;
   target.title = 'Estimated weekdays, excluding weekends. Holidays and destination may affect timing.';
  });
 }
 mobile.addEventListener('change', () => { document.querySelectorAll('[data-sfg-routine]').forEach(root => selectStep(root,Number(root.dataset.active))); release(current);current=null;sync(); });
 reduced.addEventListener('change', () => { document.querySelectorAll('[data-sfg-routine]').forEach(clearStepMotion); films.forEach(state => { state.user=false; }); preferred=null;sync(); });
 connection?.addEventListener('change', sync);
 const clearPressedButtons = () => document.querySelectorAll('[data-sfg-pressed]').forEach(button => delete button.dataset.sfgPressed);
 window.addEventListener('blur',clearPressedButtons);
 document.addEventListener('visibilitychange',() => { if (document.hidden) { clearPressedButtons(); document.querySelectorAll('[data-sfg-routine]').forEach(clearStepMotion); } sync(); });
 document.addEventListener('shopify:section:load',init);
 document.addEventListener('shopify:section:unload',() => requestAnimationFrame(sync));
 if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',init,{once:true}); else init();
})();
